import { InstallmentsIcon, LockIcon, NoReturnIcon, OrderIcon, ReturnIcon } from '@lastbrand/web-ui/icons'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import { STATSIG_EXPERIMENTS } from '@src/config/statsig-experiments.config'
import { useStatsigExperimentBucket } from '@src/experiments/statsig-integration/useStatsigExperiment'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, {
  CUSTOM_FIELD_NAMES,
  getIsWhiteGloveDelivery,
  isProductAlcohol,
  isProductLuggage,
} from '@src/quince-site-common/src/helpers/productHelper'
import * as svdCustomFields from '@src/quince-site-common/src/helpers/svd-custom-fields'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { TrackConfig } from '../../hocs/withTracking'
import { Image } from '../image'
import { default as Modal } from '../modal'
import { ProductInfoGroup, ProductInfoItem } from '../productInfoGroup'

import { ICON_CONSTANTS, ICON_CONSTANTS_MAP } from './icon-constants'
import AgeRestricted from './icons/ageRestricted.inline.svg'

export interface ProductDescFooterProps {
  hideFooter: boolean | undefined
  isReturnable: boolean | undefined
  product: Product | undefined
  selectedOptions?: ProductVariantOption[]
  isFinalSaleProduct?: boolean
  isB2BSelfService?: boolean
  // SVD flow: read age-restriction / age-limit custom fields SVD-native. Threaded from `product-form-ds`
  // (main buy box); defaults false so the legacy form and miniPDP renders stay on the legacy reads.
  isSvd?: boolean
}

const containerCss = css({ width: '100%', marginTop: 'space-10' })

const svgIconCss = css({ width: '20px', height: '20px' })

// The luggage warranty shield has no @lastbrand/web-ui icon yet, so it stays a Contentful image.
const contentfulIconCss = css({ width: '20px', height: '20px', objectFit: 'contain' })

const desktopOnlyCss = css({ lgDown: { display: 'none' } })

const mobileOnlyCss = css({ lg: { display: 'none' } })

const easyReturnsTriggerCss = css({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const easyReturnModalCss = css({
  height: '185px',
  width: '476px',
  padding: 'space-48',
  lgDown: {
    width: '100vw',
    height: '205px',
    paddingBlock: 'space-48',
    paddingInline: 'space-16',
    marginTop: 'calc(100vh - 190px)',
  },
})

const easyReturnModalWrapperCss = css({
  lgDown: { background: 'darkgray-trans-50' },
})

const easyReturnModalHeaderCss = css({
  textStyle: 'body-14',
  fontWeight: 500,
  letterSpacing: '0.06em',
  textAlign: 'left',
  marginBottom: 'space-8',
})

const easyReturnModalSubTextCss = css({
  textStyle: 'body-12',
  textAlign: 'left',
  marginBottom: 'space-16',
})

const easyReturnModalLinkCss = css({
  borderBottom: '1px solid token(colors.black)',
  fontSize: '11px',
  fontWeight: 400,
  lineHeight: '14px',
  textAlign: 'left',
})

export const ProductDescFooterDS = ({
  hideFooter,
  isReturnable,
  product,
  selectedOptions,
  isFinalSaleProduct,
  isB2BSelfService,
  isSvd = false,
}: ProductDescFooterProps) => {
  const { t } = useTranslation('common')
  const [showEasyReturnModal, setShowEasyReturnModal] = useState(false)
  const secondChargingExpBucket = useStatsigExperimentBucket(STATSIG_EXPERIMENTS.SECOND_RETURNS_CHARGING)
  const isInChargingExpBucket = secondChargingExpBucket === 'Show'
  const isAgeRestrictedProduct = isSvd
    ? svdCustomFields.getSvdIsAgeRestrictedProduct(product)
    : productHelper?.getIsAgeRestrictedProduct(product as Product)
  const ageLimitInYears = isSvd
    ? svdCustomFields.getSvdAgeLimitInYears(product)
    : product
      ? productHelper?.getCustomFieldValue(product, CUSTOM_FIELD_NAMES.AGE_LIMIT_IN_YEARS, false, true)
      : undefined
  if (hideFooter) {
    return <></>
  }
  const isWhiteGloveDelivery = getIsWhiteGloveDelivery(product)
  const isLuggageProduct = isProductLuggage(product)

  const easyReturnsTrackConfig: TrackConfig = {
    trackGA: true,
    trackInternal: true,
    // @i18n-ignore
    category: 'checkout',
    // @i18n-ignore
    action: 'easy_returns',
    context: {
      product_id: product?.spid,
      title: product?.title,
      tags: product?.tags,
      subClassId: product?.subClassId,
      color: selectedOptions?.find(option => option.name === 'Color')?.value,
      size: selectedOptions?.find(option => option.name === 'Size')?.value,
    },
  }

  const handleEasyReturnClick = () => {
    setShowEasyReturnModal(prev => !prev)
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, easyReturnsTrackConfig)
  }

  const paymentOrWarrantyTitle = isLuggageProduct ? t('limited_lifetime_warranty') : t('secure_payment')
  const paymentOrWarrantyLabel = isLuggageProduct ? (
    <>
      <span className={desktopOnlyCss}>{t('limited_lifetime_warranty')}</span>
      <span className={mobileOnlyCss}>{t('ltd_lifetime_warranty')}</span>
    </>
  ) : (
    t('secure_payment')
  )

  return (
    <div
      data-testid="product-desc-footer"
      data-hide-footer={hideFooter?.toString()}
      data-is-returnable={isReturnable?.toString()}
      className={containerCss}
    >
      <ProductInfoGroup variant="pdp">
        <ProductInfoItem icon={<OrderIcon size="20px" />}>
          {isWhiteGloveDelivery ? t('free_white_glove_delivery') : t('free_standard_shipping')}
        </ProductInfoItem>

        {isReturnable && !product?.hasRestockingFee ? (
          isFinalSaleProduct && !isB2BSelfService ? (
            <ProductInfoItem icon={<InstallmentsIcon size="20px" />}>{t('pay_in_installments')}</ProductInfoItem>
          ) : !isB2BSelfService ? (
            <ProductInfoItem icon={<ReturnIcon size="20px" />}>
              {isInChargingExpBucket ? (
                <span
                  role="button"
                  tabIndex={0}
                  className={easyReturnsTriggerCss}
                  onClick={handleEasyReturnClick}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleEasyReturnClick()
                    }
                  }}
                >
                  {t('easy_returns_v2')}
                </span>
              ) : (
                t('easy_returns_v2')
              )}
            </ProductInfoItem>
          ) : null
        ) : (
          !isB2BSelfService && (
            <ProductInfoItem
              icon={
                isAgeRestrictedProduct ? <AgeRestricted className={svgIconCss} /> : <InstallmentsIcon size="20px" />
              }
            >
              {isAgeRestrictedProduct
                ? ageLimitInYears
                  ? t('age_restricted_product', { ageLimitInYears })
                  : t('age_restricted_product_v2')
                : t('pay_in_installments')}
            </ProductInfoItem>
          )
        )}

        {isB2BSelfService && (
          <ProductInfoItem icon={<InstallmentsIcon size="20px" />}>{t('pay_in_installments')}</ProductInfoItem>
        )}

        <ProductInfoItem
          icon={
            isLuggageProduct ? (
              <Image
                url={ICON_CONSTANTS_MAP[ICON_CONSTANTS.SHIELD_ICON]}
                title={paymentOrWarrantyTitle}
                alt=""
                className={contentfulIconCss}
                useNative
              />
            ) : (
              <LockIcon size="20px" />
            )
          }
        >
          {paymentOrWarrantyLabel}
        </ProductInfoItem>

        {!isReturnable && isProductAlcohol(product) && (
          <ProductInfoItem icon={<NoReturnIcon size="20px" />}>{t('not_returnable_food_safety')}</ProductInfoItem>
        )}

        {!isReturnable && !isAgeRestrictedProduct && (
          <ProductInfoItem icon={<NoReturnIcon size="20px" />}>{t('this_item_is_not_returnable')}</ProductInfoItem>
        )}
      </ProductInfoGroup>

      {showEasyReturnModal && isInChargingExpBucket && (
        <Modal
          open={showEasyReturnModal}
          className={easyReturnModalCss}
          modalWrapperClassName={easyReturnModalWrapperCss}
          showCloseButton={true}
          onClose={handleEasyReturnClick}
        >
          <div>
            <h3 className={easyReturnModalHeaderCss}>{t('return_policy')}</h3>
            <div className={easyReturnModalSubTextCss}>{t('first_return_free_policy')}</div>
            <a
              className={easyReturnModalLinkCss}
              // @i18n-ignore
              href={`${window.location.origin}/shipping-returns`}
            >
              {t('learn_more')}
            </a>
          </div>
        </Modal>
      )}
    </div>
  )
}
